import { Api } from '../../Api';
import { DataFrame, Method, Status } from '../../DataFrame';
import { DependencyContainer } from '../../DependencyContainer';
import { ITypedEvent, TypedEvent } from '../../TypedEvent';
import { ControllerConfigurationService } from '../Controller/ControllerConfigurationService';
import { IControllerConfigurationService } from '../Controller/IControllerConfigurationService';
import { ExalusConnectionService } from '../ExalusConnectionService';
import { FieldChangeResult, FieldChangeResultType } from '../FieldChangeResult';
import { IExalusConnectionService } from '../IExalusConnectionService';
import { ILoggerService } from '../Logging/ILoggerService';
import { LoggerService } from '../Logging/LoggerService';
import { IWebApiCacheService } from '../WebApi/IWebApiCacheService';
import { WebApiCacheService } from '../WebApi/WebApiCacheService';
import { DevicesService } from './DevicesService';
import { CommunicationWay, DeviceResponseType, DeviceState, DeviceTaskType, DeviceType, IconType, IDevice, IDeviceResponseTypeInfo, IDeviceTaskTypeInfo } from './IDevice';
import { IDeviceChannel } from './IDeviceChannel';
import { IDevicesService } from './IDevicesService';
import { IDeviceState, IDeviceStateData } from './IDeviceState';
import { IDeviceTask } from './IDeviceTask';
import { DeviceTaskExecutionResult } from './TaskExecutionResult';

export class Device implements IDevice {
    private static _devicesService: DevicesService | null;
    private _onDeviceStateChangedEvent: ITypedEvent<IDeviceState<IDeviceStateData>> = new TypedEvent<IDeviceState<IDeviceStateData>>();
    private _onDeviceStateRefreshedOrChangedEvent: ITypedEvent<IDeviceState<IDeviceStateData>> = new TypedEvent<IDeviceState<IDeviceStateData>>();
    private _onDeviceTasksExecutionChangedOnChannelsEvent: ITypedEvent<number[]> = new TypedEvent<number[]>();
    private _onDeviceFirmwareVersionChangedEvent: ITypedEvent<{ FirmwareVersion: string }> = new TypedEvent<{ FirmwareVersion: string }>();
    private _guid?: string;
    private _name?: string;
    private _iconType: IconType = IconType.Unknown;
    private _channelsAmount?: number;
    private _serialNumber: string | null = null;
    private _model: string | null = null;
    private _modelGuid: string | null = null;
    private _manufacturerGuid: string | null = null;
    private _isVirtual: boolean = false;
    private _isEnabled: boolean = false;
    private _deviceState: DeviceState = DeviceState.Working;
    private _deviceType: DeviceType = DeviceType.Unknown;
    private _communicationWay: CommunicationWay = CommunicationWay.OneWay;
    private _availableTasksTypes: IDeviceTaskTypeInfo[] = [];
    private _availableResponseTypes: IDeviceResponseTypeInfo[] = [];
    private _channels: IDeviceChannel[] = [];
    private _protocolGuid: string | null = null;
    private _softwareVersion: string | null = null;

    constructor() {
        if (Device._devicesService == null)
            Device._devicesService = Api.Get<DevicesService>(DevicesService.ServiceName);

        const stateChangedEvent = this._onDeviceStateChangedEvent as TypedEvent<IDeviceState<IDeviceStateData>>;
        const tasksExecutionChangeEvent = this._onDeviceTasksExecutionChangedOnChannelsEvent as TypedEvent<number[]>;

        Device._devicesService.OnDeviceStateChangedEvent().Subscribe(stateChange => {
            if (stateChange.Device.Guid != this._guid)
                return;
            
            stateChangedEvent.Invoke(stateChange.State);
        });

        Device._devicesService.OnDeviceStateRefreshedOrChangedEvent().Subscribe(stateChange => {
            if (stateChange.Device.Guid != this._guid)
                return;
            
            (this._onDeviceStateRefreshedOrChangedEvent as TypedEvent<IDeviceState<IDeviceStateData>>).Invoke(stateChange.State);
        });

        Device._devicesService.OnDevicesTasksExecutionChangeEvent().Subscribe(tasks => {
            tasksExecutionChangeEvent.Invoke(tasks.filter(task => task.DeviceGuid == this._guid).map(task => task.Channel));
        });
    }

    OnDeviceFirmwareVersionChangedEvent(): ITypedEvent<{ FirmwareVersion: string; }> {
        return this._onDeviceFirmwareVersionChangedEvent;
    }

    OnDeviceStateRefreshedOrChangedEvent(): ITypedEvent<IDeviceState<IDeviceStateData>> {
        return this._onDeviceStateRefreshedOrChangedEvent;
    }

    get ShouldChannelsBeGrouped(): boolean {
        if (this._deviceType === DeviceType.Remote) {
            switch (this._modelGuid) {
                case "afea26ac-d5de-4aa9-8e74-ff69679d097b":
                case "908a71ec-ca46-4431-9504-05b7801e77f8":
                case "d8fea502-541e-42d9-8ece-a1bcfaaa93f9":
                    return false;

                default:
                    return this._availableTasksTypes.all(a => a.Type !== DeviceTaskType.SetBlindPositionSimple);
            }
        }

        return false;
    }

    get States(): IDeviceState<IDeviceStateData>[] {
        if (LoggerService.DoesThrowStackTrace)
            return (Api.Get<DevicesService>(DevicesService.ServiceName)).GetDeviceStates(this).selectMany(a => a.States).toArray();
        else
            return (Device._devicesService as DevicesService).GetDeviceStates(this).selectMany(a => a.States).toArray();
    }

    RemoveDeviceAsync(): Promise<DeviceTaskExecutionResult> {
        return (Device._devicesService as DevicesService).RemoveDeviceAsync(this);
    }

    ExecuteDeviceTaskAsync(task: IDeviceTask): Promise<DeviceTaskExecutionResult> {
        return (Device._devicesService as DevicesService).ExecuteDeviceTaskAsync(this, task);
    }

    OnDeviceTasksExecutionChangedOnChannelsEvent(): ITypedEvent<number[]> {
        return this._onDeviceTasksExecutionChangedOnChannelsEvent;
    }

    OnDeviceStateChangedEvent(): ITypedEvent<IDeviceState<any>> {
        return this._onDeviceStateChangedEvent;
    }

    public get Guid(): string {
        return this._guid == null ? "" : this._guid;
    }

    public set Guid(guid: string) {
        this._guid = guid;
    }

    public get IconType(): IconType {
        return this._iconType;
    }

    public set IconType(iconType: IconType) {
        this._iconType = iconType;
    }

    public get Name(): string {
        return this._name == null ? "" : this._name;
    }

    public set Name(name: string) {
        this._name = name;
    }

    public set SoftwareVersion(version: string | null) {
        this._softwareVersion = version;
    }

    public get SoftwareVersion(): string | null {
        return this._softwareVersion;
    }

    get ChannelsAmount(): number {
        return this._channelsAmount == null ? 0 : this._channelsAmount;
    }

    public set ChannelsAmount(amount: number) {
        this._channelsAmount = amount;
    }

    get SerialNumber(): string | null {
        return this._serialNumber;
    }

    public set SerialNumber(serialNumber: string | null) {
        this._serialNumber = serialNumber;
    }

    get Model(): string | null {
        return this._model;
    }

    public set Model(model: string | null) {
        this._model = model;
    }

    get ModelGuid(): string | null {
        return this._modelGuid;
    }

    public set ModelGuid(model: string | null) {
        this._modelGuid = model;
    }

    get ManufacturerGuid(): string | null {
        return this._manufacturerGuid;
    }

    set ManufacturerGuid(manufacturerGuid: string | null) {
        this._manufacturerGuid = manufacturerGuid;
    }

    get IsVirtual(): boolean {
        return this._isVirtual;
    }

    set IsVirtual(isVirtual: boolean) {
        this._isVirtual = isVirtual;
    }

    get IsEnabled(): boolean {
        return this._isEnabled;
    }

    set IsEnabled(isEnabled: boolean) {
        this._isEnabled = isEnabled;
    }

    get DeviceState(): DeviceState {
        return this._deviceState;
    }

    set DeviceState(deviceState: DeviceState) {
        this._deviceState = deviceState;
    }

    get DeviceType(): DeviceType {
        return this._deviceType;
    }

    set DeviceType(deviceType: DeviceType) {
        this._deviceType = deviceType;
    }

    get CommunicationWay(): CommunicationWay {
        return this._communicationWay;
    }

    set CommunicationWay(communicationWay: CommunicationWay) {
        this._communicationWay = communicationWay;
    }

    get AvailableTaskTypes(): IDeviceTaskTypeInfo[] {
        return this._availableTasksTypes;
    }

    set AvailableTaskTypes(availableTasksTypes: IDeviceTaskTypeInfo[]) {
        this._availableTasksTypes = availableTasksTypes;
    }

    get AvailableResponseTypes(): IDeviceResponseTypeInfo[] {
        return this._availableResponseTypes;
    }

    set AvailableResponseTypes(availableResponseTypes: IDeviceResponseTypeInfo[]) {
        this._availableResponseTypes = availableResponseTypes;
    }

    get Channels(): IDeviceChannel[] {
        return this._channels;
    }

    get ProtocolGuid(): string | null {
        return this._protocolGuid;
    }

    set ProtocolGuid(value: string | null) {
        this._protocolGuid = value;
    }

    async ChangeDeviceNameAsync(name: string): Promise<FieldChangeResult> {
        try {
            const result = await Api.Get<IExalusConnectionService>(ExalusConnectionService.ServiceName)
                .SendAndWaitForResponseAsync(new DeviceChannelConfigurationRequest(this, name), 20000, false);

            switch (result.Status) {
                case Status.OK:
                    this._name = name;

                    DependencyContainer.Log?.Error("Device", `EXIT CONFIGURATION MODE WILL BE FIRED! ChangeDeviceNameAsync()`);
                    Api.Get<IControllerConfigurationService>(ControllerConfigurationService.ServiceName).ExitConfigurationModeAsync();
                    return new FieldChangeResult(FieldChangeResultType.Changed, "");

                case Status.WrongData:
                    if (result.Data == "DeviceNotFound")
                        return new FieldChangeResult(FieldChangeResultType.Failed, "DeviceNotFound");
                    break;

                case Status.NoPermissionsToCallGivenResource:
                    return new FieldChangeResult(FieldChangeResultType.NoPermissions, "");


            }
        }
        catch (ex: any) {
            if ((ex as Error).message.includes("timeout"))
                return new FieldChangeResult(FieldChangeResultType.ControllerResponseTimeout, "");

            return new FieldChangeResult(FieldChangeResultType.UnknownError, "");
        }

        return new FieldChangeResult(FieldChangeResultType.Unknown, "");
    }

    ExecuteTaskAsync(task: IDeviceTask): Promise<DeviceTaskExecutionResult> {
        return (Device._devicesService as DevicesService).ExecuteDeviceTaskAsync(this, task);
    }
}


class DeviceChannelConfigurationRequest extends DataFrame<DeviceNameData>
{
    constructor(device: IDevice, name: string) {
        super();
        this.Resource = "/devices/device/name";
        this.Method = Method.Put;
        const data = new DeviceNameData();
        data.DeviceGuid = device.Guid;
        data.Name = name;
        this.Data = data;
    }
}

class DeviceNameData {
    DeviceGuid: string = "";
    Name: string = "";
}